import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Row, Col, Space, Button, Card } from 'antd';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { type ComboOptionInput, ComboType } from '../../../../api/types';
import ArrayField from '../../../../components/fields/ArrayField';
import CompanyCurrencyField from '../../../../components/fields/CompanyCurrencyField';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import SelectField from '../../../../components/fields/SelectField';
import TranslatedInputField from '../../../../components/fields/TranslatedInputField';
import TranslatedTextAreaFieldArray from '../../../../components/fields/TranslatedTextAreaFieldArray';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import { useVariantConfigurator } from '../VariantConfiguratorContext';
import { type OptionBlockFormValues, getDefaultOptionComboTypeInput } from './utils';

type OptionComboTypeFieldRendererType = {
    prefix: string;
    reference: string;
    disabled?: boolean;
};

const OptionComboTypeFieldRenderer = ({ prefix, reference, disabled = false }: OptionComboTypeFieldRendererType) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);

    const [field] = useField<ComboType>(reference);

    switch (field.value) {
        case ComboType.Dropdown:
            return (
                <Col lg={8} xs={24}>
                    <TranslatedTextAreaFieldArray
                        {...t('variantConfiguratorDetails:fields.options.description', {
                            returnObjects: true,
                        })}
                        disabled={disabled}
                        itemProps={{ required: true }}
                        name={`${prefix}descriptions`}
                        showAdd={!disabled}
                        isDefault
                        required
                        withContentRefinement
                    />
                </Col>
            );

        case ComboType.TextField:
            return (
                <>
                    <Col lg={8} xs={24}>
                        <InputField
                            {...t('variantConfiguratorDetails:fields.options.placeholder', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name={`${prefix}placeholder`}
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <InputNumberField
                            {...t('variantConfiguratorDetails:fields.options.charLimit', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            name={`${prefix}charLimit`}
                        />
                    </Col>
                </>
            );

        default:
            return null;
    }
};

type OptionComboTypeFieldType = {
    name: string;
    disabled?: boolean;
};
const OptionComboTypeField = ({ name, disabled = false }: OptionComboTypeFieldType) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);

    const { configurator } = useSystemOptions();

    return (
        <ArrayField<ComboOptionInput, OptionBlockFormValues>
            name={name}
            render={({ remove, push }, options) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                    {options?.map((option, index) => (
                        <Card
                            key={option.id ?? index.toString()}
                            extra={
                                !disabled && (
                                    <Button
                                        icon={<DeleteOutlined />}
                                        onClick={() => remove(index)}
                                        type="link"
                                        danger
                                    />
                                )
                            }
                            title={t('variantConfiguratorDetails:sections.options.blockTypeSetting', {
                                number: index + 1,
                            })}
                            type="inner"
                        >
                            <Row gutter={14}>
                                <Col lg={8} xs={24}>
                                    <TranslatedInputField
                                        {...t('variantConfiguratorDetails:fields.options.optionName', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name={`${name}[${index}]label`}
                                        required
                                    />
                                </Col>
                                <Col lg={8} xs={24}>
                                    <SelectField
                                        {...t('variantConfiguratorDetails:fields.options.optionType', {
                                            returnObjects: true,
                                        })}
                                        disabled={disabled}
                                        name={`${name}[${index}]type`}
                                        options={configurator.comboOptionTypes}
                                        required
                                        showSearch
                                    />
                                </Col>
                                <OptionComboTypeFieldRenderer
                                    disabled={disabled}
                                    prefix={`${name}[${index}]`}
                                    reference={`${name}[${index}]type`}
                                />
                            </Row>
                        </Card>
                    ))}
                    {!disabled && (
                        <Button
                            icon={<PlusOutlined />}
                            onClick={() => push(getDefaultOptionComboTypeInput())}
                            type="link"
                        >
                            {t('variantConfiguratorDetails:actions.options.addComboSetting')}
                        </Button>
                    )}
                </Space>
            )}
        />
    );
};

type OptionTypeComboFieldType = {
    prefix: string;
    disabled?: boolean;
};
const OptionTypeComboField = ({ prefix, disabled = false }: OptionTypeComboFieldType) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);

    const { variantConfigurator } = useVariantConfigurator();

    return (
        <>
            <Col lg={8} xs={24}>
                <CompanyCurrencyField
                    {...t('variantConfiguratorDetails:fields.options.price', {
                        returnObjects: true,
                    })}
                    company={variantConfigurator.company}
                    disabled={disabled}
                    name={`${prefix}.price`}
                    required
                />
            </Col>
            <Col lg={24} xs={24}>
                <OptionComboTypeField disabled={disabled} name={`${prefix}.options`} />
            </Col>
        </>
    );
};

export default OptionTypeComboField;
